/* eslint-disable react/no-array-index-key */
import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import type { Image as HeroImage } from '@/interfaces/strapi';

type HeroProps = {
	readonly className?: string;
	readonly images: HeroImage[];
	readonly intervalMs?: number;
	readonly heightPx?: number;
	readonly mtOffsetPx?: number;
};

const classNameComponente = 'c-cnm-hero';

export const Hero = ({ className, images, intervalMs = 5000, heightPx = 320, mtOffsetPx = -42 }: HeroProps) => {
	const wrapperClassName = useMemo(() => classNames(classNameComponente, className), [className]);

	const [index, setIndex] = useState(0);

	// Pre-carga de imágenes
	useEffect(() => {
		images.forEach(({ url }) => {
			if (url) {
				const i = new Image();
				i.src = url;
			}
		});
	}, [images]);

	// Auto-rotación
	useEffect(() => {
		if (images.length <= 1) return;
		const id = window.setInterval(() => {
			setIndex((i) => (i + 1) % images.length);
		}, intervalMs);
		return () => window.clearInterval(id);
	}, [images.length, intervalMs]);

	if (!images?.length) {
		return (
			<div
				className={wrapperClassName}
				style={{ height: `${heightPx}px`, marginTop: `${mtOffsetPx}px` }}
				aria-label="Hero (sin imágenes)"
			/>
		);
	}

	return (
		<div
			className={wrapperClassName}
			style={{ height: `${heightPx}px`, marginTop: `${mtOffsetPx}px` }}
			aria-label="Carrusel de imágenes destacadas"
		>
			<div className={`${classNameComponente}__slides`}>
				{images.map((img, i) => (
					<div
						key={`${img.url}-${i}`}
						id={`hero-slide-${i}`}
						className={classNames(`${classNameComponente}__slide`, {
							'is-active': i === index,
						})}
						aria-hidden={i !== index}
					>
						<img
							className={`${classNameComponente}__img`}
							src={img.url}
							alt={img.alt ?? ''}
							loading={i === 0 ? 'eager' : 'lazy'}
						/>
					</div>
				))}
			</div>

			<div className={`${classNameComponente}__dots`} role="tablist" aria-label="Selector de imágenes del hero">
				{images.map((_, i) => (
					<button
						key={`dot-${i}`}
						type="button"
						className={classNames(`${classNameComponente}__dot`, {
							'is-active': i === index,
						})}
						aria-selected={i === index}
						aria-controls={`hero-slide-${i}`}
						title={`Imagen ${i + 1}`}
						onClick={() => setIndex(i)}
					/>
				))}
			</div>
		</div>
	);
};
